import { AlertTriangle, ChevronRight, Layers } from 'lucide-react';
import { useState } from 'react';
import { Explain } from '@/components/Explain';
import { JsonTree } from '@/components/JsonTree';
import { OutcomeChip } from '@/components/OutcomeChip';
import { CopyId, Money } from '@/components/primitives';
import { Section } from '@/components/Section';
import { cn } from '@/lib/cn';
import { deriveOutcome } from '@/lib/bet';
import type { TimeZoneMode } from '@/lib/format';
import type { BetBundle, RoundWager } from '@/types';
import { DataTable } from './DataTable';

/**
 * The WHOLE round, for multi-stake games (blackjack). One round holds many
 * wagers — the opening bet per box, plus every double / split / insurance — each
 * with its own bet_id and cycle. betlens's other sections show the ONE wager the
 * lookup landed on; this shows every hand of its round.
 *
 * Rendered only when `bundle.round` carries data (skipped for ordinary games).
 * The primary wager is highlighted. A losing hand's win transaction at 0 is
 * shown as-is — it is the normal close, not an unpaid bet.
 */
export function RoundSection({
  bundle,
  tz,
  onLookupRound,
}: {
  bundle: BetBundle;
  tz: TimeZoneMode;
  /** Re-run as an explicit round lookup (the "treat as round id" affordance). */
  onLookupRound?: (roundId: string) => void;
}) {
  const round = bundle.round;
  if (round.status !== 'ok' || !round.data) return null;
  const data = round.data;
  const meta = bundle.currencyMetadata.data;

  return (
    <Section
      id="section-round"
      title="Round (all hands)"
      term="round_id"
      source={round}
      count={data.wagers.length}
      note={
        <span className="font-mono text-[11px] text-text-faint">
          round {data.round_id} · {data.wagers.length}{' '}
          {data.wagers.length === 1 ? 'wager' : 'wagers'}
        </span>
      }
    >
      {/* The ambiguity affordance: the pasted id was BOTH a wager and a round. */}
      {data.ambiguous_id && onLookupRound && (
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline border-l-2 border-l-warn bg-warn-bg px-4 py-2 text-[12px]">
          <AlertTriangle size={13} className="shrink-0 text-warn" />
          <span className="text-text">
            <span className="font-mono">{data.ambiguous_id.id}</span> is also a round id. Shown here
            read as a <strong>wager</strong> (round {data.ambiguous_id.as_wager}).
          </span>
          <button
            type="button"
            onClick={() => onLookupRound(data.ambiguous_id!.as_round)}
            className="inline-flex items-center gap-1 border border-warn/50 px-2 py-[3px] font-condensed text-[10px] font-semibold uppercase tracking-wider text-warn hover:bg-warn/10"
          >
            <Layers size={11} /> treat {data.ambiguous_id.id} as a round id
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse whitespace-nowrap text-[12px]">
          <thead>
            <tr className="border-b border-hairline bg-surface-sunken">
              <Th />
              <Th align="left">
                <Explain term="stake_kind">stake</Explain>
              </Th>
              <Th align="left">hand</Th>
              <Th align="left">
                <Explain term="bet_id">bet id</Explain>
              </Th>
              <Th align="right">stake</Th>
              <Th align="right">win</Th>
              <Th align="left">status</Th>
              <Th align="left">outcome</Th>
              <Th align="right">trx</Th>
            </tr>
          </thead>
          <tbody>
            {data.wagers.map((w) => (
              <WagerRow
                key={w.bet_id}
                wager={w}
                meta={meta}
                tz={tz}
                primary={w.bet_id === data.primary_bet_id}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* The rounds-table row: authoritative totals, result, seeds. */}
      {data.round && (
        <div className="border-t border-hairline px-4 py-3">
          <div className="eyebrow mb-2">round record</div>
          <JsonTree value={data.round} defaultOpen={1} />
        </div>
      )}
    </Section>
  );
}

function WagerRow({
  wager,
  meta,
  tz,
  primary,
}: {
  wager: RoundWager;
  meta: BetBundle['currencyMetadata']['data'];
  tz: TimeZoneMode;
  primary: boolean;
}) {
  const [open, setOpen] = useState(primary);
  const betTx = wager.transactions.find((t) => t.action === 'bet');
  const winTx = wager.transactions.find((t) => t.action === 'win');
  const status = (wager.bet?.status as string | undefined) ?? '—';
  const outcome = deriveOutcome(wager.transactions);
  const stake = (wager.bet?.bet_amount as string | undefined) ?? betTx?.amount ?? null;
  const win = (wager.bet?.won_amount as string | undefined) ?? winTx?.amount ?? null;

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-b border-hairline/50 hover:bg-surface-raised/50',
          primary && 'bg-accent/5',
        )}
        onClick={() => setOpen((o) => !o)}
      >
        <td className="w-6 pl-4">
          <ChevronRight
            size={12}
            className={cn('text-text-faint transition-transform', open && 'rotate-90')}
          />
        </td>
        <td className="px-3 py-1.5">
          <span className="font-condensed text-[11px] font-semibold uppercase tracking-wider text-text">
            {wager.stake_kind ?? '—'}
          </span>
          {primary && (
            <span className="ml-2 border border-accent/40 px-1 font-condensed text-[9px] uppercase tracking-wider text-accent">
              looked up
            </span>
          )}
        </td>
        <td className="px-3 py-1.5 font-mono text-[11.5px] text-text-dim">{wager.hand_id ?? '—'}</td>
        <td className="px-3 py-1.5">
          <CopyId value={wager.bet_id} />
        </td>
        <td className="px-3 py-1.5 text-right">
          <Money amount={stake} meta={meta} currency={betTx?.currency} tone="bet" />
        </td>
        <td className="px-3 py-1.5 text-right">
          {/* A 0 here is a losing hand's normal close, not an unpaid bet. */}
          <Money amount={win} meta={meta} currency={winTx?.currency} tone="win" />
        </td>
        <td className="px-3 py-1.5 font-mono text-[11.5px] text-text-dim">{status}</td>
        <td className="px-3 py-1.5">
          <OutcomeChip outcome={outcome} />
        </td>
        <td className="px-3 py-1.5 text-right font-mono text-text-dim">{wager.transactions.length}</td>
      </tr>

      {open && (
        <tr className="border-b border-hairline/50 bg-surface-sunken">
          <td colSpan={9} className="px-4 py-2">
            <div className="mb-1.5 flex items-center gap-2 text-[11px] text-text-faint">
              <span className="eyebrow">transactions</span>
              <CopyId value={wager.cycle_uniq_id} truncate className="text-text-dim" />
            </div>
            {wager.transactions.length ? (
              <DataTable rows={wager.transactions as unknown as Record<string, unknown>[]} meta={meta} tz={tz} />
            ) : (
              <p className="text-[12px] text-text-dim">No transactions for this wager.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Th({ children, align = 'right' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={cn(
        'px-3 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}
